/**
 * Hala Control Center - Quality & Issues Management
 * Premium issue tracking and quality review interface
 */

import { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Plus,
  Search,
  Flag,
  CheckCircle,
  Clock,
  Eye,
  MessageSquare,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/control-center`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#1D8F4E',
  warning: '#F59E0B',
  error: '#EF4444',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

const SEVERITY_CONFIG = {
  low: { label: 'منخفض', color: COLORS.textMuted },
  medium: { label: 'متوسط', color: COLORS.warning },
  high: { label: 'مرتفع', color: COLORS.error },
  critical: { label: 'حرج', color: '#DC2626' }
};

const STATUS_CONFIG = {
  open: { label: 'مفتوح', color: COLORS.primary },
  under_review: { label: 'قيد المراجعة', color: COLORS.warning },
  resolved: { label: 'تم الحل', color: COLORS.success },
  closed: { label: 'مغلق', color: COLORS.textLight }
};

const ISSUE_TYPES = {
  wrong_recommendation: 'توصية خاطئة',
  missing_service: 'خدمة مفقودة',
  wrong_city_mapping: 'ربط مدينة خاطئ',
  wrong_need_classification: 'تصنيف احتياج خاطئ',
  poor_conversation: 'جودة محادثة ضعيفة',
  incomplete_session: 'جلسة غير مكتملة',
  service_data_issue: 'مشكلة بيانات خدمة',
  ui_issue: 'مشكلة واجهة',
  other: 'أخرى'
};

export default function IssuesPage() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchIssues();
  }, [statusFilter, severityFilter]);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      let url = `${API}/issues?limit=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (severityFilter) url += `&severity=${severityFilter}`;
      
      const res = await fetch(url);
      const data = await res.json();
      setIssues(data.items || []);
    } catch (e) {
      console.error('Failed to fetch issues:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (issueId, newStatus) => {
    try {
      await fetch(`${API}/issues/${issueId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchIssues();
    } catch (e) {
      console.error('Failed to update issue:', e);
    }
  };

  const stats = {
    open: issues.filter(i => i.status === 'open').length,
    under_review: issues.filter(i => i.status === 'under_review').length,
    critical: issues.filter(i => i.severity === 'critical' && i.status !== 'closed').length
  };

  const selectStyle = {
    padding: '10px 14px',
    borderRadius: '8px',
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.white,
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    cursor: 'pointer',
    minWidth: '140px'
  };

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: COLORS.text, margin: '0 0 8px' }}>مركز الجودة</h1>
        <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: 0 }}>إدارة ومراجعة المشاكل والملاحظات</p>
      </div>

      {/* Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '12px',
          padding: '20px',
          border: `1px solid ${COLORS.border}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              backgroundColor: `${COLORS.primary}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AlertTriangle size={22} color={COLORS.primary} />
            </div>
            <div>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.primary, margin: 0 }}>{stats.open}</p>
              <p style={{ fontSize: '13px', color: COLORS.textMuted, margin: 0 }}>مشاكل مفتوحة</p>
            </div>
          </div>
        </div>
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '12px',
          padding: '20px',
          border: `1px solid ${COLORS.border}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              backgroundColor: `${COLORS.warning}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Eye size={22} color={COLORS.warning} />
            </div>
            <div>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.warning, margin: 0 }}>{stats.under_review}</p>
              <p style={{ fontSize: '13px', color: COLORS.textMuted, margin: 0 }}>قيد المراجعة</p>
            </div>
          </div>
        </div>
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '12px',
          padding: '20px',
          border: `1px solid ${COLORS.border}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              backgroundColor: `${COLORS.error}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Flag size={22} color={COLORS.error} />
            </div>
            <div>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.error, margin: 0 }}>{stats.critical}</p>
              <p style={{ fontSize: '13px', color: COLORS.textMuted, margin: 0 }}>حرجة</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        border: `1px solid ${COLORS.border}`,
        display: 'flex',
        gap: '12px',
        alignItems: 'center'
      }}>
        <select 
          value={statusFilter} 
          onChange={e => setStatusFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="">كل الحالات</option>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
        
        <select 
          value={severityFilter} 
          onChange={e => setSeverityFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="">كل الخطورة</option>
          {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>

        <div style={{ marginRight: 'auto' }}>
          <button style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: COLORS.primary,
            color: COLORS.white,
            fontSize: '13px',
            cursor: 'pointer'
          }}>
            <Plus size={16} />
            إضافة مشكلة
          </button>
        </div>
      </div>

      {/* Issues List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textMuted }}>
          جاري التحميل...
        </div>
      ) : issues.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          border: `1px solid ${COLORS.border}`
        }}>
          <CheckCircle size={48} color={COLORS.success} style={{ marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: COLORS.text }}>لا توجد مشاكل</h3>
          <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>كل شيء يعمل بشكل سليم</p>
        </div>
      ) : (
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          border: `1px solid ${COLORS.border}`,
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.bg }}>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>المشكلة</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>النوع</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>الخطورة</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>الحالة</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>التاريخ</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted, width: '120px' }}>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {issues.map(issue => {
                  const severity = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.low;
                  const status = STATUS_CONFIG[issue.status] || STATUS_CONFIG.open;
                  
                  return (
                    <tr key={issue.issue_id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: '16px' }}>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: COLORS.text }}>{issue.title}</p>
                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: COLORS.textMuted, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {issue.description}
                        </p>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ fontSize: '13px', color: COLORS.textMuted }}>
                          {ISSUE_TYPES[issue.issue_type] || issue.issue_type}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          backgroundColor: `${severity.color}15`,
                          color: severity.color,
                          fontSize: '12px',
                          fontWeight: 500
                        }}>
                          {severity.label}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          backgroundColor: `${status.color}15`,
                          color: status.color,
                          fontSize: '12px',
                          fontWeight: 500
                        }}>
                          {status.label}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ fontSize: '12px', color: COLORS.textLight }}>
                          {issue.created_at ? new Date(issue.created_at).toLocaleDateString('ar-SA') : '-'}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {issue.status === 'open' && (
                            <button
                              onClick={() => handleUpdateStatus(issue.issue_id, 'under_review')}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: `${COLORS.warning}15`,
                                color: COLORS.warning,
                                fontSize: '11px',
                                cursor: 'pointer'
                              }}
                            >
                              مراجعة
                            </button>
                          )}
                          {(issue.status === 'open' || issue.status === 'under_review') && (
                            <button
                              onClick={() => handleUpdateStatus(issue.issue_id, 'resolved')}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: `${COLORS.success}15`,
                                color: COLORS.success,
                                fontSize: '11px',
                                cursor: 'pointer'
                              }}
                            >
                              حل
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
